import {
  WorkplaceRequestLocationValue,
  WorkplaceRequestTypeElement
} from "@gettactic/api";
import * as z from "zod";

// Common field schemas
const titleSchema = z
  .string()
  .min(1, "Title is required")
  .max(100, "Title must be less than 100 characters");

const descriptionSchema = z.string().optional();

const typeIdSchema = z.string().min(1, "Type is required");

const locationSchema = z.string().min(1, "Location is required");

const statusSchema = z.enum(["Open", "Pending", "Closed"]);

// Common type object schema. The form carries the full request type element
// (it is selected from the live types list), so the schema is a type carrier
// rather than a narrower shape that would diverge from WorkplaceRequestTypeElement.
const typeObjectSchema = z.custom<WorkplaceRequestTypeElement>();

// Base request schema (common fields)
const baseRequestSchema = z.object({
  title: titleSchema,
  description: descriptionSchema,
  type: typeObjectSchema
});

// Schema for creating a new workplace request
export const createWorkplaceRequestSchema = z.object({
  title: titleSchema,
  description: descriptionSchema,
  type: typeIdSchema,
  location: locationSchema,
  submitter: z.string().optional(),
  status: statusSchema,
  owner: z.string().optional()
});

/**
 * The create form as the modal validates it: the title is required unless the
 * picked type generates its own, in which case the input is not shown and
 * core renders the title from the answers. Same values as
 * {@link createWorkplaceRequestSchema}, so the form type is shared.
 */
export const createWorkplaceRequestSchemaFor = (
  isTitleGenerated: (typeId: string) => boolean
) =>
  createWorkplaceRequestSchema
    .extend({
      title: z.string().max(100, "Title must be less than 100 characters")
    })
    .superRefine((values, ctx) => {
      if (!isTitleGenerated(values.type) && values.title.length === 0) {
        ctx.addIssue({
          code: "custom",
          path: ["title"],
          message: "Title is required"
        });
      }
    });

// Schema for managing multiple workplace requests in reservation context
export const workplaceRequestsFormSchema = z.object({
  requests: z.array(
    baseRequestSchema.extend({
      id: z.string(),
      isNew: z.boolean().optional(),
      isDeleted: z.boolean().optional(),
      originalData: z.any().optional(),
      // Office/location and dynamic field answers captured by the wizard. They are
      // persisted only when the reservation is saved.
      office_id: z.string().optional(),
      location: z.custom<WorkplaceRequestLocationValue>().optional(),
      field_values: z
        .array(z.object({ field_id: z.string(), value: z.any() }))
        .optional(),
      // Raw wizard form values, used to re-hydrate the wizard when editing a draft.
      values: z.record(z.string(), z.any()).optional()
    })
  )
});

// Type exports for TypeScript
export type CreateWorkplaceRequestFormValues = z.infer<
  typeof createWorkplaceRequestSchema
>;
export type WorkplaceRequestsFormValues = z.infer<
  typeof workplaceRequestsFormSchema
>;
